import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { createFilterOptions } from "@mui/material";
import type { CommuneAssocieeDeleguee } from "models";
import { communesAssocieesDelegueesService } from "services";
import { useErrorHandler, withComponentErrorBoundary } from "utils/errorHandling";
import { stringUtil } from "@sdeapps/react-core";
import { MultiAutoComplete } from "./MultiAutoComplete";
import { grey } from "@mui/material/colors";
import { multiAutoClassNames } from "utils";

const filterOptions = createFilterOptions({
  trim: true,
  stringify: (option: CommuneAssocieeDeleguee) => {
    const normalizedString = stringUtil.normalize(option.libelle);
    return `${normalizedString} ${option.libelle}`;
  },
});

interface CommuneAssocieeDelegueeMultiAutoCompleteProps {
  value: Array<CommuneAssocieeDeleguee>;
  onChange?: (communeOuCad: Array<CommuneAssocieeDeleguee>) => void;
  label?: string;
  placeholder?: string;
  size?: "small" | "medium";
  disabled?: boolean;
}

function CommuneAssocieeDelegueeMultiAutoComplete({
  value,
  onChange,
  label,
  placeholder,
  size = "medium",
  disabled = false,
}: Readonly<CommuneAssocieeDelegueeMultiAutoCompleteProps>): ReactNode {
  const [cads, setCads] = useState<Array<CommuneAssocieeDeleguee>>([]);
  const { catchErrors, isLoading } = useErrorHandler();

  useEffect(() => {
    async function getCommunes(): Promise<void> {
      const _communes = await communesAssocieesDelegueesService.getAll();
      setCads(_communes);
    }
    void catchErrors(getCommunes);
  }, [catchErrors]);

  return (
    <MultiAutoComplete
      value={value}
      onChange={onChange}
      options={cads}
      filterOptions={filterOptions}
      size={size}
      singleItemLabel="Commune associée/déléguée"
      isLoading={isLoading}
      label={label}
      placeholder={placeholder}
      disabled={disabled}
      renderOption={(props, { id, libelle }, { selected }) => (
        <li {...props} key={id}>
          <div
            className={`${multiAutoClassNames.AUTOCOMPLETE_CSS_CHECKBOX} ${selected ? multiAutoClassNames.CHECKED : multiAutoClassNames.UNCHECKED}`}
          />
          {libelle} <span className="grey">({id.substring(0, 2)})</span>
        </li>
      )}
      getOptionKey={({ id }) => id}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      slotProps={{
        paper: { sx: { "&.MuiAutocomplete-paper li span.grey": { color: grey[500], ml: 0.5 } } },
      }}
    />
  );
}

export const CommuneAssocieeDelegueeMultiAutoCompleteWithErrorBoundary = withComponentErrorBoundary(
  CommuneAssocieeDelegueeMultiAutoComplete
);
